import throttle from 'lodash/throttle';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';

import {
  useConsumerQuery,
  useUpdateConsumerMutation,
} from 'apollo/queries/autogenerate/hooks';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import { getSeiMei } from 'shared/utils/normalize-user-name';
import validator, { Rule } from 'shared/utils/validator';

type NameProps = {
  kanjiSei: string;
  kanjiMei: string;
  kanaSei: string;
  kanaMei: string;
};

type Props = {
  /**
   * Invoked when editing is successful
   */
  onSuccess: () => void;
};

const fieldNames: Array<keyof NameProps> = [
  'kanjiSei',
  'kanjiMei',
  'kanaSei',
  'kanaMei',
];

const fieldLabels = ['姓', '名', 'セイ（カタカナ）', 'メイ（カタカナ）'];

const fieldPlaceholders = ['鈴木', '太郎', 'スズキ', 'タロウ'];

const EditUserNameForm = (props: Props) => {
  const { refetch, loading } = useConsumerQuery({
    fetchPolicy: 'network-only',
    skip: true,
  });

  const [updateConsumerMutation] = useUpdateConsumerMutation({
    update(cache) {
      refetch({
        masked: true,
      }).then(({ data }) => {
        cache.modify({
          fields: {
            consumer(cachedData) {
              return {
                ...cachedData,
                ...data?.consumer,
              };
            },
          },
        });
      });
    },
  });

  const form = useFormWithValidations({
    kanjiSei: '',
    kanaMei: '',
    kanaSei: '',
    kanjiMei: '',
  });

  useEffect(() => {
    refetch({
      masked: false,
    }).then(({ data }) => {
      const [kanjiSei, kanjiMei] = getSeiMei(data.consumer.name1 || '');
      const [kanaSei, kanaMei] = getSeiMei(data.consumer.name2 || '');

      // We set the default values here
      // So any changes afterwards will be treated "dirty" and can be handled appropriately
      form.reset({ kanjiMei, kanjiSei, kanaMei, kanaSei });
    });
  }, []);

  const submit = useCallback(
    throttle(
      async () => {
        const name1 = `${form.getValues(fieldNames[0])} ${form.getValues(
          fieldNames[1]
        )}`;
        const name2 = `${form.getValues(fieldNames[2])} ${form.getValues(
          fieldNames[3]
        )}`;

        try {
          await updateConsumerMutation({
            variables: {
              name1,
              name2,
            },
          });

          props.onSuccess();
        } catch (err) {
          /* noop */
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    []
  );

  return (
    <View
      style={style.root}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      <View>
        {fieldNames.map((field, index) => (
          <TextInput
            key={index}
            value={form.getValues(field)}
            label={fieldLabels[index]}
            placeholder={fieldPlaceholders[index]}
            onChangeText={(text) => form.setValue(field, text)}
            onBlur={() => form.triggerValidation(field)}
            message={(form.errors[field]?.message as string) || ''}
            state={form.errors[field]?.message ? 'error' : undefined}
            editable={!loading}
            style={style.marginBottom}
            id={`edit-user-name-${field.toLowerCase()}-field`}
          />
        ))}
      </View>
      <View>
        <Button
          title="次へ"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            isValid && submit();
          }}
          style={style.nextButton}
          id="edit-user-name-save-button"
          disabled={!form.formState.dirty}
        />
      </View>
    </View>
  );
};

const useFormWithValidations = (props: NameProps) => {
  const form = useForm<NameProps>({
    defaultValues: props,
  });

  const kanjiRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainNonPrintableChars',
  ];

  const kanaRules: Rule[] = [...kanjiRules, 'mustBeKatakanaOrSpaces'];

  useEffect(() => {
    fieldNames.forEach((name) => {
      form.register(
        {
          name,
        },
        {
          validate:
            name === fieldNames[0] || name === fieldNames[1]
              ? validator.getRules(kanjiRules)
              : validator.getRules(kanaRules),
        }
      );
    });
  }, [form.register]);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  marginBottom: {
    marginBottom: 24,
  },
  nextButton: {
    alignSelf: 'center',
  },
});

export default EditUserNameForm;
export { NameProps };
